/**
 * Admin: per-Service-Group assessment library + defaults configuration.
 *
 * Two side-by-side checklists driven from the TestDefinition catalogue.
 *   Library  — testKeys allowed in this group (compliance/scope ceiling).
 *   Defaults — subset of library granted to every therapist automatically.
 *
 * Per-therapist additional grants (not configured here) live on the clinical
 * supervisor's Therapist Detail view.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Checkbox, FormControlLabel, Button, Alert,
  CircularProgress, Divider, Chip
} from '@mui/material';
import api from '../../../services/api';

export default function ServiceGroupAssessmentSettings({ groupId, token, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [library, setLibrary] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [catalogue, setCatalogue] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/assessment-governance/admin/service-groups/${groupId}/assessments`, token);
        if (cancelled) return;
        setLibrary(res?.data?.library || []);
        setDefaults(res?.data?.defaults || []);
        setCatalogue(res?.data?.catalogue || []);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Could not load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, token]);

  function toggleLibrary(testKey) {
    setLibrary((prev) => {
      const next = prev.includes(testKey) ? prev.filter(k => k !== testKey) : [...prev, testKey];
      // If removed from library, also remove from defaults
      if (!next.includes(testKey)) setDefaults(d => d.filter(k => k !== testKey));
      return next;
    });
  }

  function toggleDefault(testKey) {
    if (!library.includes(testKey)) return; // can't default outside library
    setDefaults((prev) => prev.includes(testKey) ? prev.filter(k => k !== testKey) : [...prev, testKey]);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(
        `/assessment-governance/admin/service-groups/${groupId}/assessments`,
        { library, defaults },
        token
      );
      onClose?.({ saved: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Library</strong> = which assessments are available in this group (compliance ceiling).{' '}
        <strong>Default</strong> = which of those are granted to every therapist automatically.
        Per-therapist add-ons live on the Clinical Supervisor's therapist page.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip size="small" label={`Library: ${library.length}`} color="primary" variant="outlined" />
        <Chip size="small" label={`Default for all therapists: ${defaults.length}`} color="success" variant="outlined" />
        <Chip size="small" label={`Catalogue total: ${catalogue.length}`} variant="outlined" />
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Stack divider={<Divider />} spacing={0}>
        {catalogue.map((t) => {
          const inLibrary = library.includes(t.testKey);
          const inDefaults = defaults.includes(t.testKey);
          return (
            <Stack
              key={t.testKey}
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ md: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ py: 1 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">
                  {t.shortName || t.testKey}
                  {t.fullName && t.fullName !== t.shortName && ` — ${t.fullName}`}
                </Typography>
                {t.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t.description.slice(0, 160)}{t.description.length > 160 ? '…' : ''}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={inLibrary} onChange={() => toggleLibrary(t.testKey)} />}
                  label="In library"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={inDefaults}
                      disabled={!inLibrary}
                      onChange={() => toggleDefault(t.testKey)}
                    />
                  }
                  label="Default"
                />
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </Box>
  );
}
